import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { CustomerData } from "@/types/client";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Loader2, ChevronDown, ChevronUp, Ban, User, Building2, MapPin, Shield, Wheat, AlertTriangle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useEstablishment } from "@/contexts/EstablishmentContext";
import { toast } from "@/hooks/use-toast";

const clientSchema = z.object({
  firstName: z.string().trim().max(50).optional().or(z.literal("")),
  lastName: z.string().trim().max(50).optional().or(z.literal("")),
  email: z.string().trim().email("Email invalide").max(255).optional().or(z.literal("")),
  phone: z.string().trim().max(20).optional().or(z.literal("")),
  notes: z.string().max(1000).optional().or(z.literal("")),
  civility: z.string().optional().or(z.literal("")),
  company: z.string().max(100).optional().or(z.literal("")),
  professionalEmail: z.string().email("Email professionnel invalide").max(255).optional().or(z.literal("")),
  professionalPhone: z.string().max(20).optional().or(z.literal("")),
  birthdate: z.string().optional().or(z.literal("")),
  address: z.string().max(255).optional().or(z.literal("")),
  city: z.string().max(100).optional().or(z.literal("")),
  zip: z.string().max(10).optional().or(z.literal("")),
  country: z.string().max(100).optional().or(z.literal("")),
  lang: z.string().optional().or(z.literal("")),
  allergiesNotes: z.string().max(1000).optional().or(z.literal("")),
  allergiesTags: z.string().optional().or(z.literal("")),
  guestTags: z.string().optional().or(z.literal("")),
  guestStatus: z.string().optional().or(z.literal("")),
  isBlacklisted: z.boolean().optional(),
  hasNoShow: z.boolean().optional(),
}).refine(
  data =>
    (data.firstName && data.firstName.length >= 1) ||
    (data.lastName && data.lastName.length >= 1) ||
    (data.email && data.email.length >= 1) ||
    (data.phone && data.phone.length >= 1),
  {
    message: "Au moins un champ d'identification est requis (prénom, nom, email ou téléphone)",
    path: ["firstName"],
  }
);

type ClientFormData = z.infer<typeof clientSchema>;

interface CreateClientDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onClientCreated: (client: CustomerData) => void;
  initialName?: string;
}

function SectionHeader({ icon: Icon, title, expanded, onToggle }: { icon: any; title: string; expanded: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="flex items-center justify-between w-full py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
    >
      <span className="flex items-center gap-2">
        <Icon className="h-4 w-4" />
        {title}
      </span>
      {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
    </button>
  );
}

export function CreateClientDialog({
  open,
  onOpenChange,
  onClientCreated,
  initialName,
}: CreateClientDialogProps) {
  const { establishmentId } = useEstablishment();
  const [isCreating, setIsCreating] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    status: false,
    allergies: false,
    professional: false,
    address: false,
  });

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Parse initialName into firstName/lastName
  const parseInitialName = () => {
    if (!initialName?.trim()) return { firstName: "", lastName: "" };
    const parts = initialName.trim().split(/\s+/);
    if (parts.length === 1) return { firstName: parts[0], lastName: "" };
    return { firstName: parts[0], lastName: parts.slice(1).join(" ") };
  };

  const { firstName: defaultFirstName, lastName: defaultLastName } = parseInitialName();

  const form = useForm<ClientFormData>({
    resolver: zodResolver(clientSchema),
    defaultValues: {
      firstName: defaultFirstName,
      lastName: defaultLastName,
      email: "",
      phone: "",
      notes: "",
      civility: "",
      company: "",
      professionalEmail: "",
      professionalPhone: "",
      birthdate: "",
      address: "",
      city: "",
      zip: "",
      country: "",
      lang: "fr",
      allergiesNotes: "",
      allergiesTags: "",
      guestTags: "",
      guestStatus: "",
      isBlacklisted: false,
      hasNoShow: false,
    },
  });

  // Reset form when dialog opens with new initialName
  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen) {
      const { firstName, lastName } = parseInitialName();
      form.reset({
        firstName,
        lastName,
        email: "",
        phone: "",
        notes: "",
        civility: "",
        company: "",
        professionalEmail: "",
        professionalPhone: "",
        birthdate: "",
        address: "",
        city: "",
        zip: "",
        country: "",
        lang: "fr",
        allergiesNotes: "",
        allergiesTags: "",
        guestTags: "",
        guestStatus: "",
        isBlacklisted: false,
        hasNoShow: false,
      });
      setEmailError(null);
      setExpandedSections({
        status: false,
        allergies: false,
        professional: false,
        address: false,
      });
    }
    onOpenChange(isOpen);
  };

  const parseTags = (str: string | undefined): string[] =>
    (str || "").split(",").map(t => t.trim()).filter(Boolean);

  const onSubmit = async (data: ClientFormData) => {
    setIsCreating(true);
    setEmailError(null);

    try {
      const normalizedEmail = (data.email || "").toLowerCase().trim();

      // Check for duplicate email
      if (normalizedEmail) {
        const { data: existing } = await supabase
          .from('customers')
          .select('id')
          .eq('email', normalizedEmail)
          .eq('establishment_id', establishmentId)
          .maybeSingle();

        if (existing) {
          setEmailError("Un client avec cet email existe déjà");
          return;
        }
      }

      const insertData: any = {
        establishment_id: establishmentId,
        first_name: (data.firstName || "").trim(),
        last_name: (data.lastName || "").trim(),
        email: normalizedEmail || null,
        phone: (data.phone || "").trim() || null,
        notes: (data.notes || "").trim() || null,
        civility: data.civility || null,
        company: (data.company || "").trim() || null,
        professional_email: (data.professionalEmail || "").trim() || null,
        professional_phone: (data.professionalPhone || "").trim() || null,
        birthdate: data.birthdate || null,
        address: (data.address || "").trim() || null,
        city: (data.city || "").trim() || null,
        zip: (data.zip || "").trim() || null,
        country: (data.country || "").trim() || null,
        lang: data.lang || "fr",
        allergies_notes: (data.allergiesNotes || "").trim() || null,
        allergies_tags: parseTags(data.allergiesTags),
        guest_tags: parseTags(data.guestTags),
        guest_status: data.guestStatus || null,
        is_blacklisted: data.isBlacklisted || false,
        has_no_show: data.hasNoShow || false,
      };

      const { data: created, error } = await supabase
        .from('customers')
        .insert(insertData)
        .select()
        .single();

      if (error) {
        console.error('[CreateClientDialog] Insert error:', error);
        setEmailError("Erreur lors de la création du client");
        return;
      }

      // Build CustomerData object for parent
      const newClient: CustomerData = {
        id: created.id,
        firstName: created.first_name,
        lastName: created.last_name,
        email: created.email || "",
        phone: created.phone || undefined,
        notes: created.notes || undefined,
        civility: created.civility || undefined,
        company: created.company || undefined,
        professionalEmail: created.professional_email || undefined,
        professionalPhone: created.professional_phone || undefined,
        birthdate: created.birthdate || undefined,
        address: created.address || undefined,
        city: created.city || undefined,
        zip: created.zip || undefined,
        country: created.country || undefined,
        lang: created.lang || undefined,
        allergiesNotes: created.allergies_notes || undefined,
        allergiesTags: created.allergies_tags || [],
        guestTags: created.guest_tags || [],
        guestStatus: created.guest_status || undefined,
        isBlacklisted: created.is_blacklisted,
        hasNoShow: created.has_no_show,
        marketingConsent: created.marketing_consent,
        smsConsent: created.sms_consent,
        arrivedReservations: 0,
        totalReservations: 0,
        totalPersons: 0,
        lastReservationDate: new Date(),
        firstReservationDate: new Date(),
        allReservations: [],
      };

      toast({
        title: "Client créé",
        description: `${created.first_name} ${created.last_name} a été ajouté.`,
      });

      onClientCreated(newClient);
      onOpenChange(false);
    } catch (error) {
      console.error('[CreateClientDialog] Unexpected error:', error);
      setEmailError("Une erreur inattendue s'est produite");
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[520px] max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Nouveau client</DialogTitle>
          <DialogDescription>
            Créez un nouveau client pour votre établissement.
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto max-h-[65vh] pr-4">
          <Form {...form}>
            <form id="create-client-form" onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              {/* === Identity === */}
              <div className="grid grid-cols-3 gap-3">
                <FormField
                  control={form.control}
                  name="civility"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Civilité</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value} disabled={isCreating}>
                        <FormControl>
                          <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="mr">M.</SelectItem>
                          <SelectItem value="mrs">Mme</SelectItem>
                          <SelectItem value="other">Autre</SelectItem>
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Prénom</FormLabel>
                      <FormControl><Input placeholder="Jean" {...field} disabled={isCreating} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nom</FormLabel>
                      <FormControl><Input placeholder="Dupont" {...field} disabled={isCreating} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl><Input type="email" {...field} disabled={isCreating} /></FormControl>
                    <FormMessage />
                    {emailError && <p className="text-sm text-destructive mt-1">{emailError}</p>}
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-2 gap-3">
                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Téléphone</FormLabel>
                      <FormControl><Input type="tel" {...field} disabled={isCreating} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="lang"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Langue</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value} disabled={isCreating}>
                        <FormControl>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="fr">Français</SelectItem>
                          <SelectItem value="en">English</SelectItem>
                          <SelectItem value="de">Deutsch</SelectItem>
                          <SelectItem value="it">Italiano</SelectItem>
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="notes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Notes client</FormLabel>
                    <FormControl>
                      <Textarea placeholder="Préférences, notes importantes..." className="min-h-[60px] resize-none" {...field} disabled={isCreating} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Separator />

              {/* === Status & Flags === */}
              <SectionHeader icon={Shield} title="Statut & Signalements" expanded={expandedSections.status} onToggle={() => toggleSection('status')} />
              {expandedSections.status && (
                <div className="space-y-3 pl-1">
                  <FormField
                    control={form.control}
                    name="guestStatus"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Statut client</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value} disabled={isCreating}>
                          <FormControl>
                            <SelectTrigger><SelectValue placeholder="Non défini" /></SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="new">Nouveau</SelectItem>
                            <SelectItem value="returning">Client récurrent</SelectItem>
                            <SelectItem value="vip">VIP</SelectItem>
                            <SelectItem value="press">Presse</SelectItem>
                            <SelectItem value="influencer">Influenceur</SelectItem>
                          </SelectContent>
                        </Select>
                      </FormItem>
                    )}
                  />
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Ban className="h-4 w-4 text-destructive" />
                      <span className="text-sm">Client blacklisté</span>
                    </div>
                    <FormField
                      control={form.control}
                      name="isBlacklisted"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <Switch checked={field.value} onCheckedChange={field.onChange} disabled={isCreating} />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-amber-500" />
                      <span className="text-sm">A fait un no-show</span>
                    </div>
                    <FormField
                      control={form.control}
                      name="hasNoShow"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <Switch checked={field.value} onCheckedChange={field.onChange} disabled={isCreating} />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              )}

              <Separator />

              {/* === Allergies & Tags === */}
              <SectionHeader icon={Wheat} title="Allergies & Préférences" expanded={expandedSections.allergies} onToggle={() => toggleSection('allergies')} />
              {expandedSections.allergies && (
                <div className="space-y-3 pl-1">
                  <FormField
                    control={form.control}
                    name="allergiesNotes"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Notes allergies</FormLabel>
                        <FormControl>
                          <Textarea placeholder="Intolérance au gluten, allergie aux noix..." className="min-h-[60px] resize-none" {...field} disabled={isCreating} />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="allergiesTags"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Tags allergies (séparés par des virgules)</FormLabel>
                        <FormControl><Input placeholder="gluten, noix, lactose" {...field} disabled={isCreating} /></FormControl>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="guestTags"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Tags client (séparés par des virgules)</FormLabel>
                        <FormControl><Input placeholder="habitué, brunch, terrasse" {...field} disabled={isCreating} /></FormControl>
                      </FormItem>
                    )}
                  />
                </div>
              )}

              <Separator />

              {/* === Professional === */}
              <SectionHeader icon={Building2} title="Professionnel" expanded={expandedSections.professional} onToggle={() => toggleSection('professional')} />
              {expandedSections.professional && (
                <div className="space-y-3 pl-1">
                  <FormField
                    control={form.control}
                    name="company"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Entreprise</FormLabel>
                        <FormControl><Input {...field} disabled={isCreating} /></FormControl>
                      </FormItem>
                    )}
                  />
                  <div className="grid grid-cols-2 gap-3">
                    <FormField
                      control={form.control}
                      name="professionalEmail"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Email pro</FormLabel>
                          <FormControl><Input type="email" {...field} disabled={isCreating} /></FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="professionalPhone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Tél. pro</FormLabel>
                          <FormControl><Input type="tel" {...field} disabled={isCreating} /></FormControl>
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              )}

              <Separator />

              {/* === Address === */}
              <SectionHeader icon={MapPin} title="Adresse & Localisation" expanded={expandedSections.address} onToggle={() => toggleSection('address')} />
              {expandedSections.address && (
                <div className="space-y-3 pl-1">
                  <FormField
                    control={form.control}
                    name="birthdate"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Date de naissance</FormLabel>
                        <FormControl><Input type="date" {...field} disabled={isCreating} /></FormControl>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="address"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Adresse</FormLabel>
                        <FormControl><Input {...field} disabled={isCreating} /></FormControl>
                      </FormItem>
                    )}
                  />
                  <div className="grid grid-cols-3 gap-3">
                    <FormField
                      control={form.control}
                      name="zip"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>NPA</FormLabel>
                          <FormControl><Input {...field} disabled={isCreating} /></FormControl>
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="city"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Ville</FormLabel>
                          <FormControl><Input {...field} disabled={isCreating} /></FormControl>
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="country"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Pays</FormLabel>
                          <FormControl><Input {...field} disabled={isCreating} /></FormControl>
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              )}
            </form>
          </Form>
        </div>
        <DialogFooter className="pt-4">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isCreating}>
            Annuler
          </Button>
          <Button type="submit" form="create-client-form" disabled={isCreating}>
            {isCreating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Création...
              </>
            ) : (
              "Créer le client"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
